import {PackageType} from '@sollapay/enums';

import type {TrustTypeStrategy} from '@sollapay/domain/trust-types';
import type {IconName} from '@sollapay/ui/components';

export enum TrustDetailsStep {
  TRUST_SCOPE = 'trust-scope',
  ASSET_DETAILS = 'asset-details',
  SELLER_DETAILS = 'seller-details'
}

export enum ComplianceStep {
  FATCA_CRS = 'fatca-crs',
  SUPPLEMENTARY = 'supplementary',
  SUPPORTING_DOCUMENTS = 'supporting-documents'
}

export type StepMetadata = {
  labelKey: string;
};

export type StepsMap = Record<string, StepMetadata>;

export const ACTIVATION_PACKAGE_TABS_METADATA: Record<
  PackageType,
  {icon: IconName; labelKey: string; items: StepsMap; comingSoon?: boolean}
> = {
  [PackageType.TRUST_DETAILS]: {
    icon: 'sollapay:trust-details',
    labelKey: 'activation.packageFormModal.tabs.trustDetails',
    items: {
      [TrustDetailsStep.TRUST_SCOPE]: {
        labelKey: 'activation.packageFormModal.steps.trustScope'
      },
      [TrustDetailsStep.ASSET_DETAILS]: {
        labelKey: 'activation.packageFormModal.steps.assetDetails'
      },
      [TrustDetailsStep.SELLER_DETAILS]: {
        labelKey: 'activation.packageFormModal.steps.sellerDetails'
      }
    }
  },
  [PackageType.COMPLIANCE]: {
    icon: 'sollapay:compliance',
    labelKey: 'activation.packageFormModal.tabs.compliance',
    items: {
      [ComplianceStep.FATCA_CRS]: {
        labelKey: 'activation.packageFormModal.steps.fatcaCrs'
      },
      [ComplianceStep.SUPPLEMENTARY]: {
        labelKey: 'activation.packageFormModal.steps.supplementary'
      },
      [ComplianceStep.SUPPORTING_DOCUMENTS]: {
        labelKey: 'activation.packageFormModal.steps.supportingDocuments'
      }
    }
  },
  [PackageType.KYC]: {
    icon: 'sollapay:kyc',
    labelKey: 'activation.packageFormModal.tabs.kyc',
    items: {},
    comingSoon: true
  }
};

/**
 * `TRUST_DETAILS`'s step list isn't static — the asset step only exists for trust types that
 * define one shared asset at activation (every general-sale strategy; not developer sale, whose
 * assets are per-purchase). Every other tab's steps are strategy-independent.
 */
export const getStepsMapForTab = (tab: PackageType, strategy: TrustTypeStrategy): StepsMap => {
  const {items} = ACTIVATION_PACKAGE_TABS_METADATA[tab];
  if (tab !== PackageType.TRUST_DETAILS || strategy.requiresAssetAtActivation) return items;

  return Object.fromEntries(
    Object.entries(items).filter(([step]) => step !== TrustDetailsStep.ASSET_DETAILS)
  );
};

export const getStepKeysForTab = (tab: PackageType, strategy: TrustTypeStrategy): string[] =>
  Object.keys(getStepsMapForTab(tab, strategy));

export const ACTIVATION_PACKAGE_CANCEL_BUTTON_METADATA = {
  labelKey: 'activation.packageFormModal.cancel',
  isDestructive: true
} as const;

export const ACTIVATION_PACKAGE_SAVE_BUTTON_METADATA = {
  labelKey: 'activation.packageFormModal.save'
} as const;
